import { centerSelectedTab } from '../../utils/scroll';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../ui/cn';
import { useSeen, useTabSwap } from '../ui/useTabSwap';
import type { ShowcaseEntry } from './showcase';

/** Contour de marque en haut à droite, dessiné quand la section apparaît. */
function Outline() {
  return (
    <svg aria-hidden viewBox="0 0 180 240" className="pointer-events-none absolute right-0 top-6 hidden h-60 w-44 text-brand lg:block" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="miter">
      <path className="cs-line" pathLength={1} d="M79 0H180M79 0L0 78L157 80V237L180 215" />
    </svg>
  );
}

/**
 * Mise en page large: petit titre qui pivote en 3D, grand titre qui monte dans un masque, contour dessiné,
 * onglets défilants avec fondus aux bords, photo qui déborde jusqu'au bord gauche de l'écran et texte à droite.
 * Au changement d'onglet: glissement des photos avec profondeur, texte qui sort puis entre, trait d'onglet.
 */
export default function BleedShowcase({ eyebrow, heading, entries, imageHeight = 'h-[22rem] sm:h-[30rem] lg:h-[34rem]' }: { eyebrow: string; heading: string; entries: ShowcaseEntry[]; imageHeight?: string }) {
  const { currentId, prevId, dir, revealed, swapping, select } = useTabSwap(entries.map((e) => e.id));
  const [setRef, seen] = useSeen<HTMLElement>();
  const bar = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ left: false, right: false });

  const syncFade = () => {
    const el = bar.current;
    if (el) setFade({ left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 });
  };
  useEffect(() => {
    syncFade();
    const el = bar.current;
    if (!el) return;
    const ro = new ResizeObserver(syncFade);
    ro.observe(el);
    return () => ro.disconnect();
  }, [entries.length]);

  // L'onglet choisi reste visible dans la rangée défilante
  useEffect(() => {
    centerSelectedTab(bar.current);
  }, [currentId]);

  if (entries.length === 0) return null;
  const current = entries.find((e) => e.id === currentId) ?? entries[0];
  const previous = entries.find((e) => e.id === prevId) ?? null;
  const playReveal = seen && !revealed;

  return (
    <section ref={setRef} data-seen={seen} className="relative overflow-hidden bg-surface py-16 text-black md:py-24">
      <Outline />
      <div className="site-container relative">
        <div className="cs-eyebrow relative flex items-center text-xs font-semibold uppercase tracking-[0.0625rem] text-ink">
          <span aria-hidden className="cs-eyebrow-line absolute right-full top-1/2 mr-6 hidden h-[0.12rem] -translate-y-1/2 bg-charcoal lg:block" />
          <span className="cs-eyebrow-text inline-block">{eyebrow}</span>
        </div>
        <div className="cs-mask mt-4 max-w-4xl pb-2">
          <h2 className="cs-title text-inherit font-display text-[2rem] font-bold normal-case leading-[1.1] tracking-normal md:text-[3rem]">{heading}</h2>
        </div>

        <div className="relative mt-10 md:mt-12">
          <div ref={bar} onScroll={syncFade} className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div role="tablist" className="flex min-w-max gap-x-10 pb-1 md:gap-x-12">
              {entries.map((e, i) => {
                const active = e.id === current.id;
                const leaving = e.id === prevId;
                return (
                  <button
                    key={e.id}
                    role="tab"
                    aria-selected={active}
                    data-visible={seen}
                    onClick={() => select(e.id)}
                    style={{ transitionDelay: seen ? `${i * 90}ms` : undefined }}
                    className={cn('ps-tab-item relative shrink-0 whitespace-nowrap pb-2.5 text-left font-display text-lg font-semibold transition-colors', active ? 'text-charcoal' : 'text-muted hover:text-charcoal')}
                  >
                    {e.label}
                    <span
                      key={active ? `in-${current.id}-${swapping}` : leaving ? 'out' : 'idle'}
                      aria-hidden
                      className={cn('absolute bottom-0 left-0 h-0.5 bg-brand', active && (swapping ? 'ps-bar-in' : 'w-full'), leaving && 'ps-bar-out')}
                      style={active ? undefined : { width: leaving ? undefined : 0 }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
          {fade.left && <span aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-surface to-transparent" />}
          {fade.right && <span aria-hidden className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-surface to-transparent" />}
        </div>
      </div>

      {/* La photo déborde jusqu'au bord gauche de l'écran; le texte reprend l'alignement du conteneur */}
      <div className="mt-8 grid items-start md:mt-10 lg:grid-cols-2 lg:items-center">
        <div className={cn('ps-stage relative overflow-hidden bg-secondary-200', imageHeight, playReveal && 'ps-reveal-img')} style={seen || revealed ? undefined : { clipPath: 'inset(0 100% 0 0)' }}>
          {previous && (
            <div key={`out-${previous.id}`} className={cn('ps-img', dir === 'next' ? 'ps-img-out-next' : 'ps-img-out-prev')}>
              <img src={previous.image} alt="" className="h-full w-full object-cover" />
            </div>
          )}
          <div key={`in-${current.id}`} className={cn('ps-img', swapping && (dir === 'next' ? 'ps-img-in-next' : 'ps-img-in-prev'))}>
            <img src={current.image} alt={current.alt} loading="lazy" className="h-full w-full object-cover" />
          </div>
        </div>

        <div className="px-6 pt-10 md:px-8 lg:pl-[8.25rem] lg:pr-[max(2rem,calc((100vw-72rem)/2))] lg:pt-0">
          <div className="relative grid [&>*]:col-start-1 [&>*]:row-start-1">
            {previous && <div key={`t-out-${previous.id}`} className="ps-text-out pointer-events-none">{previous.content}</div>}
            <div
              key={`t-in-${current.id}`}
              className={cn(swapping ? (dir === 'next' ? 'ps-text-in-next' : 'ps-text-in-prev') : playReveal ? 'ps-reveal-text' : !seen && !revealed && 'opacity-0')}
            >
              {current.content}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
